/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-empty-object-type, @typescript-eslint/no-unsafe-function-type, @typescript-eslint/no-wrapper-object-types */
/* eslint-disable complexity */
import React, { ReactNode } from 'react';
import clsx from 'clsx';

import { ForecastComparisonCellMeta } from '@futrli-sdk/predict';

import Icon from 'carbon-react/lib/components/icon';

import { FinData, FinFieldOptions, ROUNDING_OPTIONS, VALUE_DISPLAY_OPTIONS } from '../../../types';
import { checkForArrowsInColumn, mapClassifierToTableRowOption } from '../../../utils-data';

import styles from '../report-table.scss';
import { TableField } from './pagination';

export interface TableCell {
  key: string;
  value: string | ReactNode;
  width?: string | number;
  classes: string;
}

export interface TableColumn {
  key: string;
  type: string;
  level: number;
  cells: TableCell[];
  collapsible?: boolean;
  svgClasses?: string;
}

const getArrowIcon = (direction: string, changeType: string, arrowClassName: string) => {
  const color = changeType === 'good' ? '#4DBF9F' : changeType === 'bad' ? '#C7384F' : '#000';
  return <Icon type={direction === 'increase' ? 'arrow_up' : 'arrow_down'} color={color} className={arrowClassName} />;
};

export const buildCellValue = (
  rawValue: string | number,
  currentColumnMeta: Record<string, unknown> | undefined,
  fieldDisplayType: string | undefined,
  rowUnit: VALUE_DISPLAY_OPTIONS,
  rowRounding: ROUNDING_OPTIONS,
  columnIndex: number,
  allFieldData: FinData | undefined,
  columnsNumber: number,
  currency: string,
  roundAndFormatValue: Function,
  intl: Record<string, unknown>
): ReactNode => {
  const d = rawValue || 'nan';
  const isVariance = currentColumnMeta?.cell_type === 'variance';
  let valueDisplay = isVariance ? currentColumnMeta?.display_type : fieldDisplayType;
  if (valueDisplay === VALUE_DISPLAY_OPTIONS.NUMBER && rowUnit) {
    valueDisplay = rowUnit;
  }

  const showArrow =
    d !== 'nan' &&
    (currentColumnMeta?.change_direction === 'increase' || currentColumnMeta?.change_direction === 'decrease');
  const arrowExistsInColumn = checkForArrowsInColumn(columnIndex, allFieldData);

  return (
    <>
      {roundAndFormatValue(
        d,
        isVariance ? ROUNDING_OPTIONS.INTEGER : rowRounding,
        (valueDisplay as VALUE_DISPLAY_OPTIONS) || VALUE_DISPLAY_OPTIONS.CURRENCY,
        intl,
        currency,
        true,
        100 / (columnsNumber + 2) < 15
      )}
      {showArrow &&
        getArrowIcon(
          currentColumnMeta?.change_direction as string,
          currentColumnMeta?.change_type as string,
          styles.arrowIcon
        )}
      {!showArrow && arrowExistsInColumn && <span style={{ marginRight: 14 }}></span>}
    </>
  );
};

interface BuildTableDataParams {
  fieldsByPage: TableField[][] | undefined;
  data: (FinData | undefined)[];
  forecastsData: (FinData | undefined)[];
  rowsOptions: { ref: string; options: FinFieldOptions | undefined }[];
  stylesPerType: Record<string, Record<string, string>>;
  hasComparisonData?: boolean;
  selectedForecastsLength: number;
  firstFinField: string | undefined;
  periodsType: string[];
  periodsTypeByForecast: Record<string, string[] | undefined>;
  periodsTypeByColumn: (string | undefined)[];
  columnsNumber: number;
  maxColumnsPerTable: number;
  currency: string;
  roundAndFormatValue: Function;
  intl: Record<string, unknown>;
}

export const buildTablesByPage = ({
  fieldsByPage,
  data,
  forecastsData,
  rowsOptions,
  stylesPerType,
  hasComparisonData,
  selectedForecastsLength,
  firstFinField,
  periodsType,
  periodsTypeByForecast,
  periodsTypeByColumn,
  columnsNumber,
  maxColumnsPerTable,
  currency,
  roundAndFormatValue,
  intl
}: BuildTableDataParams): { table: TableColumn[]; startColumn: number; endColumn: number }[][] => {
  if (!data?.length || !data[0] || !data[0]?.periods?.length) {
    return [];
  }

  let rowRounding = ROUNDING_OPTIONS.INTEGER;
  let rowUnit = VALUE_DISPLAY_OPTIONS.NUMBER;
  let page = 0;
  const tablesPerPage: { table: TableColumn[]; startColumn: number; endColumn: number }[][] = [];

  fieldsByPage?.forEach((fds, pageIndex) => {
    const pageTables: { table: TableColumn[]; startColumn: number; endColumn: number }[] = [];
    let table: TableColumn[] = [];

    fds.forEach((f, fi) => {
      if (f.ref === 'header') {
        if (table.length) {
          pageTables.push({
            table,
            startColumn: page * maxColumnsPerTable,
            endColumn: Math.min((page + 1) * maxColumnsPerTable, columnsNumber)
          });
          table = [];
          page += 1;
        } else if (tablesPerPage.length) {
          page += 1;
        }
      } else {
        rowRounding = rowsOptions.find(e => e.ref === f.ref)?.options?.rounding || rowRounding;
        rowUnit = rowsOptions.find(e => e.ref === f.ref)?.options?.unit || rowUnit;
        const rowStyles = stylesPerType[mapClassifierToTableRowOption(f.classifier, f.ref)];
        const evenSuf = fi % 2 ? 'Even' : '';
        const row: TableColumn = {
          type: f.classifier,
          level: f.level,
          key: `${f.ref}#level-${f.level}`,
          collapsible: f.collapsible,
          svgClasses: clsx(rowStyles.svgStyle),
          cells: [
            {
              key: `table-${pageIndex}-column-${f.ref}-${fi}`,
              value: f.name,
              classes: clsx(styles.leftAlignedCell, rowStyles.row, fi % 2 ? rowStyles.rowEven : null)
            }
          ]
        };

        if (!hasComparisonData) {
          data[0]?.periods.forEach((_, i) => {
            forecastsData.forEach((fdata, j) => {
              const rawValue = fdata?.fields[f.ref]?.[i] || 'nan';
              const currentColumnMeta = fdata?.field_meta[f.ref]?.column_meta[i];
              const isVariance = currentColumnMeta?.cell_type === 'variance';
              const periodT = periodsTypeByForecast[fdata?.forecastId || '']?.[i] || periodsType[i];

              row.cells.push({
                key: `${f.ref}-${i}-${j}`,
                classes: clsx(
                  rowStyles.row,
                  evenSuf && rowStyles.rowEven,
                  isVariance
                    ? rowStyles[`rowVariance${evenSuf}`]
                    : rowStyles[`row${periodT}${evenSuf}` as 'rowPast' | 'rowFuture' | 'rowPastEven' | 'rowFutureEven'],
                  currentColumnMeta?.is_first_in_group ? styles.autoLeftBorder : '',
                  currentColumnMeta?.is_last_in_group ? styles.autoRightBorder : '',
                  selectedForecastsLength < 2 &&
                    firstFinField &&
                    data[0]?.field_meta[firstFinField]?.column_meta[i]?.is_first_in_group
                    ? styles.autoRightBorder
                    : ''
                ),
                value: buildCellValue(
                  rawValue,
                  (currentColumnMeta as unknown) as Record<string, unknown>,
                  fdata?.field_meta[f.ref]?.display_type,
                  rowUnit,
                  rowRounding,
                  i,
                  fdata,
                  columnsNumber,
                  currency,
                  roundAndFormatValue,
                  intl
                )
              });
            });
          });
        } else {
          data[0]?.columns?.forEach((_, i) => {
            const rawValue = data[0]?.fields[f.ref]?.[i] || 'nan';
            const currentColumnMeta = data[0]?.field_meta[f.ref]?.column_meta[i];
            const isVariance = currentColumnMeta?.cell_type === 'variance';

            row.cells.push({
              key: `${f.ref}-${i}`,
              classes: clsx(
                rowStyles.row,
                evenSuf && rowStyles.rowEven,
                isVariance
                  ? rowStyles[`rowVariance${evenSuf}`]
                  : rowStyles[
                      `row${periodsTypeByColumn?.[i] || ''}${evenSuf}` as
                        | 'rowPast'
                        | 'rowFuture'
                        | 'rowPastEven'
                        | 'rowFutureEven'
                    ],
                ((currentColumnMeta as unknown) as ForecastComparisonCellMeta)?.is_first_in_period
                  ? styles.autoLeftBorder
                  : '',
                ((currentColumnMeta as unknown) as ForecastComparisonCellMeta)?.is_last_in_period
                  ? styles.autoRightBorder
                  : '',
                selectedForecastsLength < 2 &&
                  firstFinField &&
                  data[0]?.field_meta[firstFinField]?.column_meta[i]?.is_first_in_group
                  ? styles.autoRightBorder
                  : ''
              ),
              value: buildCellValue(
                rawValue,
                (currentColumnMeta as unknown) as Record<string, unknown>,
                currentColumnMeta?.display_type,
                rowUnit,
                rowRounding,
                i,
                data[0],
                columnsNumber,
                currency,
                roundAndFormatValue,
                intl
              )
            });
          });
        }
        table.push(row);
      }
    });

    pageTables.push({
      table,
      startColumn: page * maxColumnsPerTable,
      endColumn: Math.min((page + 1) * maxColumnsPerTable, columnsNumber)
    });
    tablesPerPage.push(pageTables);
  });

  return tablesPerPage;
};
